import { useFormContext, useWatch } from "react-hook-form";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "components/text";
import { StyledCalendar } from "components/StyledCalendar";
import {
  getErrorMessage,
  parseDateRange,
  transformToInputDate,
  defaultRange,
} from "../utils";
import { DateInput } from "./DateInput";

type RangeField = "from" | "to";

export const DateRangeFilter = ({ name }: { name: string }) => {
  const { setValue } = useFormContext();
  const [activeField, setActiveField] = useState<RangeField>("from");
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const dateRange = useWatch({ name: `filters.${name}.range` }) || defaultRange;
  const { t } = useTranslation(["books"]);

  const error = getErrorMessage(dateRange, t);

  // Only a focus the user drove counts. This panel is Radix dropdown content,
  // whose focus scope refocuses a field when the panel re-renders, and honouring
  // that would retarget the next pick at the field already filled.
  const keyboardDrivenRef = useRef(false);

  const activateOnKeyboardFocus = (field: RangeField) => () => {
    if (!keyboardDrivenRef.current) return;
    keyboardDrivenRef.current = false;
    setActiveField(field);
  };

  // Fill only the currently active field with the clicked day, then hand focus
  // to the other field so the next pick completes the range.
  const handleCalendarChange = (
    _range: { from?: Date; to?: Date } | undefined,
    triggerDate: Date,
  ) => {
    setValue(`filters.${name}.range`, {
      ...dateRange,
      [activeField]: transformToInputDate(triggerDate),
    });
    const nextField: RangeField = activeField === "from" ? "to" : "from";
    setActiveField(nextField);
    (nextField === "from" ? fromRef : toRef).current?.focus();
  };

  const handleInputChange = (rawValue: string, key: RangeField) => {
    setValue(`filters.${name}.range`, { ...dateRange, [key]: rawValue });
  };

  return (
    <div
      className="flex items-center flex-col gap-2 p-2"
      onKeyDownCapture={() => {
        keyboardDrivenRef.current = true;
      }}
      onMouseDownCapture={() => {
        keyboardDrivenRef.current = false;
      }}
    >
      <DateInput
        aria-label="from date input"
        className="w-full"
        value={dateRange?.from || ""}
        inputRef={fromRef}
        onMouseDown={() => setActiveField("from")}
        onFocus={activateOnKeyboardFocus("from")}
        onChange={(rawValue) => handleInputChange(rawValue, "from")}
      />
      <DateInput
        className="w-full"
        value={dateRange?.to || ""}
        aria-label="to date input"
        inputRef={toRef}
        onMouseDown={() => setActiveField("to")}
        onFocus={activateOnKeyboardFocus("to")}
        onChange={(rawValue) => handleInputChange(rawValue, "to")}
      />
      {error && (
        <Text variant="errorSmall" className="self-start">
          {error}
        </Text>
      )}
      <StyledCalendar
        mode="range"
        selected={parseDateRange(dateRange)}
        onSelect={handleCalendarChange}
      />
    </div>
  );
};
